/**
 * Cart Remove Product Action
 * Triggered by: arc:action:cartRemoveProduct
 * Removes a product from the cart
 */

export const runCartRemoveProduct = async (data = {}) => {
    try {
        const {
            variantId,
            lineItem,
            key,
            sections = [],
            sections_url = window.location.pathname,
            updateCart = true
        } = data;

        // Determine the item identifier (prioritize key, then lineItem, then variantId)
        let itemIdentifier = key;
        
        if (!itemIdentifier && lineItem) {
            itemIdentifier = lineItem;
        }
        
        if (!itemIdentifier && variantId) {
            // If only variantId provided, we need to find the line item key
            try {
                const cartResponse = await fetch('/cart.js');
                if (cartResponse.ok) {
                    const cartData = await cartResponse.json();
                    const item = cartData.items.find(item => item.variant_id == variantId);
                    if (item) {
                        itemIdentifier = item.key;
                    }
                }
            } catch (error) {
                console.warn('Failed to fetch cart data:', error);
            }
        }

        if (!itemIdentifier) {
            throw new Error('Item identifier (key, lineItem, or variantId) is required');
        }

        // Prepare removal data
        const removeData = {
            id: itemIdentifier,
            quantity: 0, // Setting quantity to 0 removes the item
            sections: sections,
            sections_url: sections_url
        };

        // Remove from cart via Shopify Cart API
        const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify(removeData)
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to remove item from cart');
        }

        const result = await response.json();

        // Update cart UI if requested
        if (updateCart) {
            // Refresh cart data
            const cartResponse = await fetch('/cart.js');
            if (cartResponse.ok) {
                const cartData = await cartResponse.json();
                
                // Dispatch cart updated event
                window.dispatchEvent(new CustomEvent('cart:updated', {
                    detail: {
                        cart: cartData,
                        action: 'item_removed',
                        removedItem: {
                            identifier: itemIdentifier,
                            variantId
                        }
                    }
                }));
            }
        }

        // Dispatch success event
        window.dispatchEvent(new CustomEvent('cart:item_removed', {
            detail: {
                cart: result,
                removedItem: {
                    identifier: itemIdentifier,
                    variantId,
                    lineItem,
                    key
                },
                timestamp: Date.now()
            }
        }));

        return {
            success: true,
            cart: result,
            removedItem: {
                identifier: itemIdentifier,
                variantId,
                lineItem,
                key
            },
            message: 'Item removed from cart successfully'
        };

    } catch (error) {
        console.error('Error removing item from cart:', error);
        
        // Dispatch error event
        window.dispatchEvent(new CustomEvent('cart:remove_error', {
            detail: {
                error: error.message,
                variantId: data.variantId,
                lineItem: data.lineItem,
                key: data.key
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

export default runCartRemoveProduct;
